import { EPersonPhoneType } from './entities/person.enum';
import type { TRegistrationLinkData } from './entities/registration-candidate.model';
import {
  REGISTRATION_PERSON_UPDATE_FIELDS,
  type IRegistrationPerson,
  type IRegistrationPersonAddress,
  type IRegistrationPersonAddressPatch,
  type IRegistrationPersonPatch,
  type IRegistrationPersonPhone,
  type IRegistrationPersonPhonePatch,
  type IRegistrationPersonUpdatePlan,
  type TRegistrationPersonUpdateChoice,
  type TRegistrationPersonUpdateOutcome,
} from './entities/registration-person.model';

/**
 * Address types as the panel's address form and the profile read them. Not this
 * lib's `EAddressType`, which names 0 and 2 the other way round.
 */
const HOME_ADDRESS_TYPE = 1;
const BUSINESS_ADDRESS_TYPE = 2;

/** The country code the person service itself assumes for a phone stored without one. */
const DEFAULT_COUNTRY_CODE = 55;

/**
 * The fields of an entry that go back with it when its list is sent. A list of
 * what the service patches, not a copy of what it answers: derived fields and
 * the marks of its own operations never return to it.
 */
const PHONE_FIELDS = [
  'PhoneId',
  'Type',
  'Number',
  'CountryCode',
  'Confirmed',
  'Comment',
  'IsMain',
] as const;

const ADDRESS_FIELDS = [
  'AddressId',
  'Type',
  'CountryCode',
  'StreetType',
  'Street',
  'Number',
  'Complement',
  'RegionName',
  'City',
  'District',
  'ZipCode',
  'CityCode',
  'IsFiscal',
] as const;

/** The parts of an address the link brings, under the names the person service gives them. */
type TLinkAddress = Partial<
  Record<
    'ZipCode' | 'Street' | 'Number' | 'Complement' | 'City' | 'RegionName' | 'District',
    string
  >
>;

const text = (value: string | null | undefined): string => (value ?? '').trim();

const digits = (value: string | null | undefined): string => (value ?? '').replaceAll(/\D/g, '');

const sameText = (one: string | null | undefined, other: string | null | undefined): boolean =>
  text(one).toLowerCase() === text(other).toLowerCase();

/**
 * An entry as read, ready to go back: the listed fields that have a value. What
 * is `null` or `''` stays out, so the body carries neither, here as in the
 * scalars. Sending back the same values changes nothing.
 */
function echo<TEntry, TPatch>(
  entry: TEntry,
  fields: readonly (keyof TEntry & keyof TPatch)[]
): TPatch {
  const patch: Partial<Record<keyof TEntry & keyof TPatch, unknown>> = {};

  for (const field of fields) {
    const value = entry[field];
    if (value !== null && value !== undefined && value !== '') patch[field] = value;
  }

  return patch as TPatch;
}

/**
 * A list that can be sent back whole: it was read, and every entry has its id.
 * An entry without an id would be inserted again, and a list that was not read
 * would be replaced by the link's single entry. Either way the answer is
 * `null`, and that list is then left alone.
 */
function sendable<TEntry>(
  entries: TEntry[] | null | undefined,
  id: keyof TEntry
): TEntry[] | null {
  return Array.isArray(entries) && entries.every((entry) => !!entry?.[id]) ? entries : null;
}

const phonesOf = (person: IRegistrationPerson): IRegistrationPersonPhone[] =>
  Array.isArray(person?.Phones) ? person.Phones : [];

const addressesOf = (person: IRegistrationPerson): IRegistrationPersonAddress[] =>
  Array.isArray(person?.Addresses) ? person.Addresses : [];

const mainMobileOf = (phones: IRegistrationPersonPhone[]): IRegistrationPersonPhone | null => {
  const withNumber = phones.filter((phone) => !!digits(phone?.Number));

  return (
    withNumber.find((phone) => phone.Type === EPersonPhoneType.MobileNotification) ??
    withNumber.find((phone) => phone.Type === EPersonPhoneType.Mobile) ??
    null
  );
};

const mainAddressOf = (
  addresses: IRegistrationPersonAddress[]
): IRegistrationPersonAddress | null =>
  addresses.find((address) => address?.IsFiscal) ??
  addresses.find((address) => address?.Type === BUSINESS_ADDRESS_TYPE) ??
  addresses[0] ??
  null;

/**
 * The person's main mobile: the one starred in the panel as the notification
 * mobile, else the first phone of type Mobile, each only when it has a number.
 * The order the profile card shows them in, and the one the person service
 * keeps its consolidated mobile by. `null` is what the counter shows as empty.
 *
 * The one rule for the screen and for the update, so the comparison never
 * shows as empty something the update would not fill.
 */
export function registrationMainMobile(
  person: IRegistrationPerson
): IRegistrationPersonPhone | null {
  return mainMobileOf(phonesOf(person));
}

/**
 * The person's main address, by the rule of the profile: the fiscal one, else
 * the business one, else the first. `null` is a person without addresses.
 */
export function registrationMainAddress(
  person: IRegistrationPerson
): IRegistrationPersonAddress | null {
  return mainAddressOf(addressesOf(person));
}

/**
 * Crossed on purpose: in the panel `RegionName` is the neighbourhood and
 * `District` the state, and the link names them the other way round. The same
 * crossing the creation of a person makes.
 */
function linkAddressOf({ address }: TRegistrationLinkData): TLinkAddress {
  const parts: Required<TLinkAddress> = {
    ZipCode: text(address?.zipCode),
    Street: text(address?.street),
    Number: text(address?.number),
    Complement: text(address?.complement),
    City: text(address?.city),
    RegionName: text(address?.district),
    District: text(address?.regionName),
  };

  return echo<Required<TLinkAddress>, TLinkAddress>(parts, [
    'ZipCode',
    'Street',
    'Number',
    'Complement',
    'City',
    'RegionName',
    'District',
  ]);
}

/**
 * Every part the link brings is what the entry already has. A part the link
 * does not bring is not compared: there is nothing to put in its place.
 */
function isLinkAddress(address: IRegistrationPersonAddress, link: TLinkAddress): boolean {
  return (Object.keys(link) as (keyof TLinkAddress)[]).every((part) =>
    part === 'ZipCode'
      ? digits(address?.ZipCode) === digits(link.ZipCode)
      : sameText(address?.[part], link[part])
  );
}

/**
 * The country code of the link's number. A link without one, or with a 0, which
 * is the code of no country, is read as the person service reads a phone stored
 * without one. The one reading for what is compared and for what is written:
 * with two, the update would not know its own phone on the next read.
 */
const linkCountryCodeOf = (link: TRegistrationLinkData): number =>
  link.phoneCountryCode || DEFAULT_COUNTRY_CODE;

function isLinkPhone(phone: IRegistrationPersonPhone, link: TRegistrationLinkData): boolean {
  return (
    digits(phone?.Number) === digits(link.phoneNumber) &&
    (phone?.CountryCode ?? DEFAULT_COUNTRY_CODE) === linkCountryCodeOf(link)
  );
}

/**
 * The link's address can take the place of a stored one: it says at least the
 * street and the city. A zip code alone, or a few parts, laid over an address
 * the person has would leave an address of two places.
 */
const isWholeAddress = (link: TLinkAddress): boolean => !!link.Street && !!link.City;

/**
 * What one choice of the counter does to a person who already exists, for the
 * registration that person sent through a link: the body of
 * `PATCH /PersonWithIncludes`, and what becomes of each datum.
 *
 * Pure, and the single place where these rules live. The screen calls it to
 * tell the attendant what a choice writes before the click; the store calls it
 * again, on a fresh read, to write exactly that.
 *
 * - Nothing the person already has is ever replaced, under either choice, but
 *   for the main mobile and the main address under `overwrite`. The names, the
 *   birthdate and the gender are only filled. A PATCH writes a name as sent,
 *   outside the panel's rule of one name change a month, and the old one could
 *   not be brought back.
 * - The e-mail is the login. It is written only on a person who has none, and
 *   only when `emailAllowed`.
 * - The body is built by omission. No key is `null` or `''`, and none of
 *   `RegistrationType`, `PersonType`, `IsOwner`, `IsActive`, `FiscalIdentity`
 *   or `Origin` is ever in it: on a visitor, the first would convert it.
 * - `Phones` and `Addresses` replace the stored list when present, so each goes
 *   only when one of its entries changes or is added, and then with every
 *   entry the person has. The main mobile and the main address are replaced in
 *   place, under their own id.
 * - What the link brings and the person already has, in any entry, is never
 *   added again. That is also what makes a second run write nothing: after the
 *   body is applied, the same call answers `body: null`.
 *
 * An address replaced in place keeps the parts the link does not bring, such as
 * a complement, the street type and the city code: taking them away would need
 * the `null` this body never sends. So the link's address takes the place of a
 * stored one only when it says at least the street and the city. With less, the
 * person's address is kept under `overwrite` too.
 */
export function buildRegistrationPersonUpdate(
  person: IRegistrationPerson,
  link: TRegistrationLinkData,
  choice: TRegistrationPersonUpdateChoice,
  { emailAllowed }: { emailAllowed: boolean }
): IRegistrationPersonUpdatePlan {
  const writes = choice !== 'none';

  /** A datum that is only ever filled: every one but the mobile and the address. */
  const filled = (
    linkHas: boolean,
    personHas: boolean,
    same: boolean
  ): TRegistrationPersonUpdateOutcome => {
    if (!linkHas) return 'absent';
    if (!personHas) return writes ? 'fill' : 'keep';
    return same ? 'same' : 'keep';
  };

  /** An entry of a list: added when the person has no main one, replaced under `overwrite`. */
  const entry = (
    linkHas: boolean,
    list: unknown[] | null,
    onRecord: boolean,
    hasMain: boolean
  ): TRegistrationPersonUpdateOutcome => {
    if (!linkHas) return 'absent';
    if (!list) return 'keep';
    if (onRecord) return 'same';
    if (!writes) return 'keep';
    if (!hasMain) return 'fill';
    return choice === 'overwrite' ? 'replace' : 'keep';
  };

  const firstName = text(link.firstName);
  const lastName = text(link.lastName);
  const birthdate = text(link.birthdate);
  const email = text(link.email);
  const gender = link.genderType ?? null;

  const phones = sendable(person.Phones, 'PhoneId');
  const mainMobile = mainMobileOf(phones ?? []);
  const addresses = sendable(person.Addresses, 'AddressId');
  const mainAddress = mainAddressOf(addresses ?? []);
  const linkAddress = linkAddressOf(link);

  const emailOutcome = filled(!!email, !!text(person.Email), sameText(person.Email, email));
  const addressOutcome = entry(
    Object.keys(linkAddress).length > 0,
    addresses,
    !!addresses?.some((address) => isLinkAddress(address, linkAddress)),
    !!mainAddress
  );

  const fields: IRegistrationPersonUpdatePlan['fields'] = {
    firstName: filled(!!firstName, !!text(person.FirstName), sameText(person.FirstName, firstName)),
    lastName: filled(!!lastName, !!text(person.LastName), sameText(person.LastName, lastName)),
    // The person's comes with a time part and the link's is `yyyy-MM-dd`.
    birthdate: filled(
      !!birthdate,
      !!text(person.Birthdate),
      text(person.Birthdate).slice(0, 10) === birthdate.slice(0, 10)
    ),
    gender: filled(
      gender !== null,
      (person.GenderType ?? null) !== null,
      person.GenderType === gender
    ),
    email: emailOutcome === 'fill' && !emailAllowed ? 'refused' : emailOutcome,
    mobile: entry(
      !!digits(link.phoneNumber),
      phones,
      !!phones?.some((phone) => isLinkPhone(phone, link)),
      !!mainMobile
    ),
    address: addressOutcome === 'replace' && !isWholeAddress(linkAddress) ? 'keep' : addressOutcome,
  };

  if (
    !REGISTRATION_PERSON_UPDATE_FIELDS.some(
      (field) => fields[field] === 'fill' || fields[field] === 'replace'
    )
  ) {
    return { body: null, fields };
  }

  const body: IRegistrationPersonPatch = { PersonId: person.PersonId };

  if (fields.firstName === 'fill') body.FirstName = firstName;
  if (fields.lastName === 'fill') body.LastName = lastName;
  if (fields.birthdate === 'fill') body.Birthdate = birthdate;
  if (fields.gender === 'fill' && gender !== null) body.GenderType = gender;
  if (fields.email === 'fill') body.Email = email;

  if (phones && (fields.mobile === 'fill' || fields.mobile === 'replace')) {
    // A number that changed is a number nobody confirmed.
    const linkPhone: IRegistrationPersonPhonePatch = {
      Number: text(link.phoneNumber),
      CountryCode: linkCountryCodeOf(link),
      Confirmed: false,
    };

    body.Phones = [
      ...phones.map((phone) => {
        const entryAsRead = echo<IRegistrationPersonPhone, IRegistrationPersonPhonePatch>(
          phone,
          PHONE_FIELDS
        );
        return fields.mobile === 'replace' && phone === mainMobile
          ? { ...entryAsRead, ...linkPhone }
          : entryAsRead;
      }),
      ...(fields.mobile === 'fill' ? [{ Type: EPersonPhoneType.Mobile, ...linkPhone }] : []),
    ];
  }

  if (addresses && (fields.address === 'fill' || fields.address === 'replace')) {
    body.Addresses = [
      ...addresses.map((address) => {
        const entryAsRead = echo<IRegistrationPersonAddress, IRegistrationPersonAddressPatch>(
          address,
          ADDRESS_FIELDS
        );
        return fields.address === 'replace' && address === mainAddress
          ? { ...entryAsRead, ...linkAddress }
          : entryAsRead;
      }),
      ...(fields.address === 'fill' ? [{ Type: HOME_ADDRESS_TYPE, ...linkAddress }] : []),
    ];
  }

  return { body, fields };
}
